/**
 * LocationSchemaSection — what this unit records, read-only.
 *
 * There is no editor here, and that is the design. A unit's shape is the
 * platform base unified with the layer rows on its ancestor chain, and
 * unification is not a text field. What an org admin needs on this panel is the
 * ANSWER — the
 * fields a write to this unit carries, which of them are demanded, and what a
 * closed set allows — so that is what it shows.
 *
 * One limit, stated rather than papered over:
 *
 *  - **The document is resolved for the CALLER.** A schema is a property of
 *    (resource × profession × unit), so an admin who is a nurse sees the nurse
 *    view of this unit and not a physician's. The heading says whose view it is
 *    rather than implying the list is everyone's.
 */

import { useTranslation } from 'react-i18next';
import { usePatientsSchema } from '@/hooks/usePatients';
import { readableFields, writableFields } from '@/api/patientSchema';
import { humaniseAttributeKey } from '@/lib/patientLabel';
import { fieldLabelKey } from '@/components/clinical/rjsfShadcn';
import type { PatientAttributeField } from '@/api/types';
import type { Location } from '@/api/endpoints/org';
import { EmptyState, ErrorState, LoadingRows, Panel, SectionHeader } from './OrgUi';

export function LocationSchemaSection({ location }: { location: Location }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = usePatientsSchema(location.id);

  // The WRITE view (`$defs.create`) is what a form at this unit renders; the
  // READ view (`$defs.item`) additionally carries what the caller may see but
  // not write — a server-minted token, most of all. Both are worth showing,
  // because "you can read this and not write it" is a real property of a unit.
  const writable = writableFields(data);
  const readOnly = readableFields(data).filter((f) => !writable.some((w) => w.key === f.key));

  return (
    <Panel className="p-4">
      <SectionHeader
        title={t('org.locationDetail.schema.title', 'Patient fields recorded here')}
        count={writable.length + readOnly.length}
      />
      <p className="mt-2 text-xs text-neutral-400">
        {t(
          'org.locationDetail.schema.yourView',
          'Resolved for your own profession — another profession records a different set.',
        )}
      </p>

      {isLoading ? (
        <LoadingRows />
      ) : isError ? (
        <ErrorState error={error} />
      ) : writable.length === 0 && readOnly.length === 0 ? (
        <EmptyState
          title={t('org.locationDetail.schema.empty', 'No patient fields at this unit')}
          hint={t(
            'org.locationDetail.schema.emptyHint',
            'The platform base declares none for your profession, and no layer on this unit adds one.',
          )}
        />
      ) : (
        <div className="mt-3 space-y-4">
          <FieldList fields={writable} />
          {readOnly.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                {t('org.locationDetail.schema.readOnly', 'Readable, not writable')}
              </p>
              <FieldList fields={readOnly} />
            </div>
          )}
        </div>
      )}

    </Panel>
  );
}

function FieldList({ fields }: { fields: PatientAttributeField[] }) {
  const { t } = useTranslation();
  if (fields.length === 0) return null;
  return (
    <ul className="divide-y divide-neutral-100">
      {fields.map((field) => (
        <li key={field.key} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 py-2">
          <span className="text-sm font-medium text-neutral-900">
            {t(
              fieldLabelKey(field.key, { label: field.label }),
              humaniseAttributeKey(field.key),
            )}
          </span>
          <span className="font-mono text-xs text-neutral-400">{field.key}</span>
          {field.required && (
            <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600">
              {t('common.required')}
            </span>
          )}
          {field.strategy === 'minted' && (
            <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[11px] text-neutral-600">
              {t('org.locationDetail.schema.minted', 'server-minted')}
            </span>
          )}
          {field.values && field.values.length > 0 && (
            <span className="text-xs text-neutral-500">{field.values.join(' · ')}</span>
          )}
          {!field.values && field.pattern && (
            <span className="font-mono text-xs text-neutral-500">{field.pattern}</span>
          )}
          {(field.classes ?? []).map((cls) => (
            <span key={cls} className="text-[11px] uppercase tracking-wide text-neutral-400">
              {cls}
            </span>
          ))}
        </li>
      ))}
    </ul>
  );
}
